"use client";

import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const ROWS = [
	{ keyPrefix: "processSelectionRow1", costStars: 2 },
	{ keyPrefix: "processSelectionRow2", costStars: 3 },
	{ keyPrefix: "processSelectionRow3", costStars: 5 },
	{ keyPrefix: "processSelectionRow4", costStars: 4 },
	{ keyPrefix: "processSelectionRow5", costStars: 2 },
	{ keyPrefix: "processSelectionRow6", costStars: 2 },
] as const;

const COLUMNS = [
	"colProcess",
	"colChars",
	"colMaterials",
	"colAccuracy",
	"colFinish",
	"colVolume",
	"colCost",
	"colAdvantages",
] as const;

const FIELD_SUFFIXES = [
	"Name",
	"Chars",
	"Materials",
	"Accuracy",
	"Finish",
	"Volume",
	null,
	"Advantages",
] as const;

function Stars({ filled, total = 5 }: { filled: number; total?: number }) {
	return (
		<span className="inline-flex gap-0.5 text-base">
			{Array.from({ length: total }, (_, i) => (
				<span key={i} className={i < filled ? "text-amber-400" : "text-gray-300"}>
					★
				</span>
			))}
		</span>
	);
}

export default function MetalCastingProcessSelection() {
	const t = useTranslations("new-metal-casting");

	return (
		<section className="bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("processSelectionTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("processSelectionDesc")}</p>
				</header>

				{/* Desktop table */}
				<div className="hidden overflow-x-auto lg:block">
					<table className="w-full border-collapse text-sm">
						<thead>
							<tr>
								{COLUMNS.map((col) => (
									<th
										key={col}
										className="border-b-2 border-[#64a525] bg-[#64a525] px-4 py-3 text-left text-base font-bold text-white"
									>
										{t(`processSelection${col.charAt(0).toUpperCase()}${col.slice(1)}`)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{ROWS.map((row, rowIdx) => (
								<tr key={row.keyPrefix} className={rowIdx === 0 ? "bg-[#f6ffde]" : "bg-white"}>
									{FIELD_SUFFIXES.map((suffix, colIdx) => (
										<td
											key={colIdx}
											className={`border-b border-gray-200 px-4 py-6 align-top ${
												colIdx === 0 ? "text-base font-bold text-black" : "text-black"
											}`}
										>
											{suffix !== null ? t(`${row.keyPrefix}${suffix}`) : <Stars filled={row.costStars} />}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{/* Mobile / Tablet cards */}
				<div className="flex flex-col gap-4 lg:hidden">
					{ROWS.map((row) => (
						<div key={row.keyPrefix} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
							<div className="bg-[#64a525] px-4 py-6">
								<h3 className="text-base font-bold text-white sm:text-lg">{t(`${row.keyPrefix}Name`)}</h3>
							</div>
							<div className="grid grid-cols-2 gap-px bg-gray-100">
								{(
									[
										["colChars", "Chars"],
										["colMaterials", "Materials"],
										["colAccuracy", "Accuracy"],
										["colFinish", "Finish"],
										["colVolume", "Volume"],
										["colCost", null],
										["colAdvantages", "Advantages"],
									] as const
								).map(([col, suffix], i) => (
									<div
										key={col}
										className={`bg-white px-4 py-3 ${col === "colAdvantages" ? "col-span-2" : ""}`}
									>
										<p className="mb-1 text-xs font-semibold text-gray-500">
											{t(`processSelection${col.charAt(0).toUpperCase()}${col.slice(1)}`)}
										</p>
										{suffix !== null ? (
											<p className="text-sm leading-relaxed text-black">{t(`${row.keyPrefix}${suffix}`)}</p>
										) : (
											<Stars filled={row.costStars} />
										)}
									</div>
								))}
							</div>
						</div>
					))}
				</div>

				{/* CTA */}
				<div className="mt-8 text-center sm:mt-10 md:mt-12">
					<p className="mb-4 text-sm leading-relaxed text-black md:text-base">
						{t("processSelectionCtaDesc")}
					</p>
					<Link
						href="/upload"
						className="inline-flex items-center justify-center rounded bg-themePrimary400 px-8 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#83b751] hover:text-white sm:text-base"
					>
						{t("processSelectionCta")}
					</Link>
				</div>
			</div>
		</section>
	);
}
